import { useCallback, useContext, useEffect, useState } from 'react';

import { useHistory, useParams } from 'react-router-dom';

import { GRID_PAGE_SIZE, TABLE_OFFSET_DELAY } from '@vyce/core/src/constants';
import {
  approveByPeriodRequest,
  approveBySiteRequest,
  getTimeLogsByLocationRequest,
  postExportTimeLogsRequest,
} from '@vyce/core/src/api/time';
import { TIME_INTERFACE_PERMISSIONS, TimeLogBySite, TimeTotalInfoProps } from '@vyce/core/src/types';
import { useBooleanState } from '@vyce/core/src/hooks/useBooleanState';
import { useDebounceValue } from '@vyce/core/src/hooks/useDebounceValue';
import { useTable } from '@vyce/core/src/hooks/useTable';

import { DeviceContext } from '@vyce/core/src/contexts';
import { downloadFileByUrl, isNil } from '@vyce/core/src/utils';
import { buildNameIdUrlSegment } from '@vyce/core/src/utils/url';
import { NotificationContext } from '@vyce/core/src/contexts/notificationContext';
import { usePermissions } from '@vyce/core/src/hooks/usePermissions';

import { getTransformedTotalData } from '../utils';
import { allLocationsId } from '../config';
import { Props } from '..';

export const useTimeLogsData = ({ companyId }: Props) => {
  const { can, canOnResource, canOnAnyResource } = usePermissions();
  const [count, setCount] = useState<number>(0);
  const { handleServerError } = useContext(NotificationContext);
  const [loading, setLoading] = useState<boolean>(true);
  const [logs, setLogs] = useState<TimeLogBySite[]>([]);
  const [totals, setTotals] = useState<TimeTotalInfoProps>();
  const history = useHistory();
  const { isMobile } = useContext(DeviceContext);
  const [approveLoading, setApproveLoading] = useState<boolean>(false);
  const [selectedLog, setSelectedLog] = useState<TimeLogBySite>();
  const [isConfirmDialogOpen, openConfirmDialog, closeConfirmDialog] = useBooleanState(false);
  const { period } = useParams<{ period: string }>();

  const { offset, substring, setOffset, handlePageChange, handleSearchChange } = useTable({});

  const dOffset = useDebounceValue(offset, TABLE_OFFSET_DELAY);

  // Column visibility: can this person approve anywhere at all.
  const cnaApproveTimelogs =
    can(TIME_INTERFACE_PERMISSIONS.APPROVE) ||
    canOnAnyResource(TIME_INTERFACE_PERMISSIONS.APPROVE, 'location');

  // Per row: the location this row is about. site_id is already the grid's row id.
  const canApproveSite = useCallback(
    (siteId?: string) =>
      can(TIME_INTERFACE_PERMISSIONS.APPROVE) ||
      canOnResource(TIME_INTERFACE_PERMISSIONS.APPROVE, 'location', siteId),
    [can, canOnResource]
  );

  const periods = period?.split(' - ');

  const getTimeLogs = useCallback(async () => {
    if (!companyId || isNil(dOffset)) {
      return;
    }

    try {
      const periods = period.split(' - ');
      setLoading(true);
      const { data } = await getTimeLogsByLocationRequest(companyId, {
        offset: dOffset as number,
        substring,
        limit: GRID_PAGE_SIZE,
        start_date: periods[0],
        end_date: periods[1],
      });

      setLogs([getTransformedTotalData(data.totals) as TimeLogBySite, ...data.items]);
      setCount(data.count);
      setTotals(data.totals);
      setLoading(false);
    } catch (e) {
      setLoading(false);
      console.error(e);
    }
  }, [companyId, dOffset, period, substring]);

  const handleApproveButtonClick = (log: TimeLogBySite) => {
    setSelectedLog(log);
    openConfirmDialog();
  };

  const handleApproveRequest = () => {
    if (selectedLog?.site_id === allLocationsId) {
      approveAllLocations();
    } else {
      approve();
    }
  };

  const approveAllLocations = async () => {
    if (!companyId || !selectedLog?.site_id) {
      return;
    }

    try {
      setApproveLoading(true);
      await approveByPeriodRequest(companyId, {
        start: periods[0],
        end: periods[1],
      });
      getTimeLogs();
      setApproveLoading(false);
      closeConfirmDialog();
    } catch (e) {
      setApproveLoading(false);
      handleServerError(e);
      console.error(e);
    }
  };

  const approve = async () => {
    if (!companyId || !selectedLog?.site_id) {
      return;
    }

    try {
      setApproveLoading(true);
      await approveBySiteRequest(companyId, selectedLog.site_id, {
        start: periods[0],
        end: periods[1],
      });
      getTimeLogs();
      setApproveLoading(false);
      closeConfirmDialog();
    } catch (e) {
      setApproveLoading(false);
      handleServerError(e);
      console.error(e);
    }
  };

  const goToByWorkerView = (name: string, id: string) => {
    if (id === allLocationsId) {
      history.push(`${history.location.pathname}/${id}`);
    } else {
      history.push(`${history.location.pathname}/${buildNameIdUrlSegment(name, id)}`);
    }
  };

  const exportTimelogs = async (log: TimeLogBySite) => {
    if (!companyId) {
      return;
    }
    try {
      const { data } = await postExportTimeLogsRequest({
        companyId,
        data: { period: { start: periods[0], end: periods[1] }, sites_ids: [log.site_id] },
      });
      downloadFileByUrl(data.url, data.file_name);
    } catch (e) {
      handleServerError(e);
    }
  };

  useEffect(() => {
    getTimeLogs();
  }, [getTimeLogs]);

  return {
    totals,
    isMobile,
    logs,
    count,
    loading,
    isConfirmDialogOpen,
    approveLoading,
    cnaApproveTimelogs,
    canApproveSite,
    offset,
    periods,
    handleApproveRequest,
    handlePageChange,
    closeConfirmDialog,
    handleSearchChange,
    goToByWorkerView,
    exportTimelogs,
    handleApproveButtonClick,
    setOffset,
  };
};
